import { useState } from 'react'
import { Spinner } from '../../../components/ui/Spinner'
import { Portal } from '../../../components/Portal'

const REMINDER_TYPES = ['registration','insurance','oil_change','inspection','other']

export const ReminderForm = ({ reminder, onSave, onClose }) => {
  const [reminderType, setReminderType] = useState(reminder?.reminder_type ?? 'other')
  const [reminderName, setReminderName] = useState(reminder?.reminder_name ?? '')
  const [dueDate,      setDueDate]      = useState(reminder?.due_date      ?? '')
  const [dueMileage,   setDueMileage]   = useState(reminder?.due_mileage   ?? '')
  const [saving,       setSaving]       = useState(false)
  const [error,        setError]        = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!reminderName.trim()) { setError('Name is required.'); return }
    if (!dueDate && !dueMileage) { setError('Set at least a due date or due mileage.'); return }
    setSaving(true)
    setError('')
    try {
      await onSave({
        reminder_type: reminderType,
        reminder_name: reminderName.trim(),
        due_date:      dueDate     || null,
        due_mileage:   dueMileage  ? parseInt(dueMileage) : null,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
  const lbl = (t) => <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{t}</label>

  return (
    <Portal>
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <h2 className="text-base font-bold text-text-primary">
            {reminder ? 'Edit Reminder' : 'Add Reminder'}
          </h2>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          <div>
            {lbl('Type')}
            <select value={reminderType} onChange={(e) => setReminderType(e.target.value)}
              className={inputCls + ' bg-surface capitalize'}>
              {REMINDER_TYPES.map((t) => (
                <option key={t} value={t} className="capitalize">{t.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>

          <div>
            {lbl('Reminder Name *')}
            <input type="text" value={reminderName} onChange={(e) => setReminderName(e.target.value)}
              placeholder="e.g. Annual registration renewal" className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              {lbl('Due Date')}
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              {lbl('Due Mileage')}
              <input type="number" value={dueMileage} onChange={(e) => setDueMileage(e.target.value)}
                placeholder="50000" className={inputCls} />
            </div>
          </div>
          <p className="text-xs text-text-muted -mt-2">At least one of date or mileage is required.</p>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">Cancel</button>
          <button type="submit" disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <><Spinner size="sm" /> Saving…</> : 'Save Reminder'}
          </button>
        </div>
      </form>
    </div>
    </Portal>
  )
}
